import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminCancelCommitmentModal from '../../components/commitments/AdminCancelCommitmentModal';
import ClientCommitmentsPanel from '../../components/commitments/ClientCommitmentsPanel';
import PageHeader from '../../components/layout/PageHeader';
import { tokens } from '../../components/forms/tokens';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatMoney } from '../../lib/datetime';
import { fullName } from '../../lib/fullName';
import authService, { type CurrentUserProfile } from '../../services/auth.service';
import commitmentsService from '../../services/commitments.service';
import depositsService from '../../services/deposits.service';
import managerService, { type ManagerAgent } from '../../services/manager.service';
import workflowService from '../../services/workflow.service';
import type { CancellationDecisionType, Commitment } from '../../types/commitments';
import type { DepositRequest } from '../../types/finance';
import { pendingDepositsForProduct } from '../../lib/commitments.helpers';
import { t } from '../../i18n/language';

/**
 * Manager queue with two distinct sections:
 *
 *  1. New commitments awaiting first approval — drafted by an agent,
 *     signed by the client, sitting in `pending_manager`. Decision
 *     hits the existing workflow:transition (`manager:approve` /
 *     `manager:reject`); the post-transition hook syncs commitment
 *     status automatically.
 *
 *  2. Cancellation requests — agent requested termination of an
 *     active|paused commitment. Manager picks one of three:
 *       · Reject (revert to active|paused)
 *       · Approve as a change (renegotiate new terms; reassign
 *         account agent)
 *       · Approve as termination (close the contract; retention
 *         applied per snapshotted product config; balance return
 *         scheduled; pending deposits auto-rejected). Renders red.
 */
export default function ManagerCommitments(): JSX.Element {
  const [me, setMe] = useState<CurrentUserProfile | null>(null);
  const [pendingApproval, setPendingApproval] = useState<Commitment[]>([]);
  const [pendingCancellation, setPendingCancellation] = useState<Commitment[]>([]);
  const [agents, setAgents] = useState<ManagerAgent[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [agentsError, setAgentsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [forceCancelTarget, setForceCancelTarget] = useState<Commitment | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const all = await commitmentsService.listPendingManager();
      setPendingApproval(all.filter((c) => c.status === 'pending_manager'));
      setPendingCancellation(all.filter((c) => c.status === 'pending_cancellation'));
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Failed to load queue'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAgents = useCallback(async () => {
    setAgentsLoading(true);
    setAgentsError(null);
    try {
      // The manager-scoped roster (GET /manager/agents) — managers can't read
      // the admin-only /admin/users list, which 403s and would silently break
      // agent reassignment on a cancellation decision.
      setAgents(await managerService.listAgents());
    } catch (e) {
      setAgentsError(
        `Could not load agents: ${getApiErrorMessage(e, 'the request failed')}. Retry to enable agent assignment.`,
      );
    } finally {
      setAgentsLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    loadAgents();
    void (async () => {
      try {
        setMe(await authService.getCurrentUser());
      } catch {
        // Non-fatal.
      }
    })();
  }, [reload, loadAgents]);

  const decideFirstApproval = async (c: Commitment, event: 'manager:approve' | 'manager:reject'): Promise<void> => {
    const reason =
      event === 'manager:reject'
        ? prompt(t('Reason for rejection (required, min 5 chars):')) ?? ''
        : '';
    if (event === 'manager:reject' && reason.trim().length < 5) return;

    setBusyId(c.id);
    setErr(null);
    setFlash(null);
    try {
      await workflowService.applyTransition(c.contract_case_id, {
        event_trigger: event,
        notes: reason || undefined,
      });
      const clientName = fullName({ first_name: c.user_first_name, last_name: c.user_last_name });
      const successMsg =
        event === 'manager:approve'
          ? `Approved commitment for ${clientName} on ${c.product_name}`
          : `Rejected commitment for ${clientName}`;
      setFlash(successMsg);
      reload();
    } catch (e) {
      setErr(getApiErrorMessage(e, 'Decision failed'));
    } finally {
      setBusyId(null);
    }
  };

  let approvalBody: JSX.Element;
  if (loading) {
    approvalBody = <p>Loading…</p>;
  } else if (pendingApproval.length === 0) {
    approvalBody = <p style={{ color: tokens.textMuted }}>Nothing pending.</p>;
  } else {
    approvalBody = (
      <div className="overflow-x-auto">
      <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Client</th>
                <th style={thStyle}>Product</th>
                <th style={thStyle}>Cadence</th>
                <th style={thStyle}>Amount</th>
                <th style={thStyle}>{t('Start', 'date')}</th>
                <th style={thStyle}>Contract</th>
                <th style={thStyle}>Decision</th>
              </tr>
            </thead>
            <tbody>
              {pendingApproval.map((c) => (
                <tr key={c.id}>
                  <td style={tdStyle}>
                    <Link
                      to={`/manager/clients/${c.user_id}`}
                      state={{ clientName: fullName({ first_name: c.user_first_name, last_name: c.user_last_name }), clientEmail: c.user_email }}
                      className="hover:underline"
                      style={{ color: tokens.textPrimary }}
                    >
                      {fullName({ first_name: c.user_first_name, last_name: c.user_last_name })}
                    </Link>
                    <br />
                    <span style={{ color: tokens.textMuted, fontSize: 11 }}>{c.user_email}</span>
                  </td>
                  <td style={tdStyle}>
                    {c.product_name}
                    <br />
                    <span style={{ color: tokens.textMuted, fontSize: 11 }}>
                      config v{c.config_version}
                    </span>
                  </td>
                  <td style={tdStyle}>{c.cadence}</td>
                  <td style={tdStyle}>
                    {formatMoney(c.amount, c.currency_code)}
                  </td>
                  <td style={tdStyle}>{c.start_date}</td>
                  <td style={tdStyle}>
                    <a
                      href={`/manager/cases/${c.contract_case_id}`}
                      style={{ color: tokens.gold }}
                    >
                      review docs
                    </a>
                  </td>
                  <td style={tdStyle}>
                    <button
                      type="button"
                      onClick={() => decideFirstApproval(c, 'manager:approve')}
                      disabled={busyId === c.id}
                      style={btnApprove}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => decideFirstApproval(c, 'manager:reject')}
                      disabled={busyId === c.id}
                      style={btnReject}
                    >
                      Reject
                    </button>
                    <button
                      type="button"
                      onClick={() => setForceCancelTarget(c)}
                      disabled={busyId === c.id}
                      style={btnForceCancel}
                      title="Emergency cancel — bypasses the normal flow (fraud / regulatory / etc)"
                    >
                      Force-cancel
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
      </div>
    );
  }

  let cancellationBody: JSX.Element;
  if (loading) {
    cancellationBody = <p>Loading…</p>;
  } else if (pendingCancellation.length === 0) {
    cancellationBody = <p style={{ color: tokens.textMuted }}>No cancellation requests.</p>;
  } else {
    cancellationBody = (
      <div style={{ display: 'grid', gap: 12 }}>
        {pendingCancellation.map((c) => (
          <CancellationCard
            key={c.id}
            c={c}
            me={me}
            agents={agents}
            agentsLoading={agentsLoading}
            agentsError={agentsError}
            onRetryAgents={() => loadAgents()}
            busy={busyId === c.id}
            onDecided={(message) => {
              setFlash(message);
              reload();
            }}
            onError={(msg) => setErr(msg)}
            setBusy={(b) => setBusyId(b ? c.id : null)}
            onForceCancel={() => setForceCancelTarget(c)}
          />
        ))}
      </div>
    );
  }

  return (
    <div style={{ padding: 24, color: tokens.textPrimary }}>
      <PageHeader
        title="Commitments — manager queue"
        subtitle="Sign off on new commitments and decide on cancellation requests"
      />

      {err ? <div style={errBoxStyle}>{err}</div> : null}
      {flash ? <div style={successBoxStyle}>{flash}</div> : null}

      <section>
        <h2 style={h2Style}>Awaiting first approval ({pendingApproval.length})</h2>
        {approvalBody}
      </section>

      <section style={{ marginTop: 32 }}>
        <h2 style={h2Style}>Cancellation requests ({pendingCancellation.length})</h2>
        {cancellationBody}
      </section>

      <section style={{ marginTop: 32 }}>
        <h2 style={h2Style}>Client commitments and monthly deposit caps</h2>
        <ClientCommitmentsPanel onFlash={setFlash} />
      </section>

      {forceCancelTarget ? (
        <AdminCancelCommitmentModal
          commitment={forceCancelTarget}
          onClose={() => setForceCancelTarget(null)}
          onCancelled={(msg) => {
            setForceCancelTarget(null);
            setFlash(msg);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

type CancellationCardProps = Readonly<{
  c: Commitment;
  me: CurrentUserProfile | null;
  agents: ManagerAgent[];
  agentsLoading: boolean;
  agentsError: string | null;
  onRetryAgents: () => void;
  busy: boolean;
  onDecided: (message: string) => void;
  onError: (msg: string) => void;
  setBusy: (b: boolean) => void;
  onForceCancel: () => void;
}>;

const DEFAULT_RETURN_DAYS = 30;

function defaultScheduledReturn(): string {
  const d = new Date();
  d.setDate(d.getDate() + DEFAULT_RETURN_DAYS);
  return d.toISOString().slice(0, 10);
}

function buildAssigneeOptions(
  me: CurrentUserProfile | null,
  agents: ManagerAgent[],
): { value: string; label: string }[] {
  const opts: { value: string; label: string }[] = [];
  if (me?.id) opts.push({ value: me.id, label: `Me (${fullName(me) || 'manager'})` });
  for (const a of agents) {
    if (a.id !== me?.id) opts.push({ value: a.id, label: `${fullName(a)} — ${a.email}` });
  }
  // Alphabetical by visible label so the dropdown reads like a sorted directory.
  return opts.sort((a, b) => a.label.localeCompare(b.label));
}

/** User-facing validation error for a cancellation decision, or null if valid. */
function validateCancellationDecision(
  decisionType: CancellationDecisionType,
  notes: string,
  renegotiationAssignee: string,
  accountAgent: string,
  scheduledReturn: string,
): string | null {
  if (notes.trim().length < 5) return 'Manager notes are required (min 5 chars).';
  if (decisionType === 'approve_change' && (!renegotiationAssignee || !accountAgent)) {
    return 'Approve as change requires both the renegotiation agent and the account agent.';
  }
  if (decisionType === 'approve_termination' && !scheduledReturn) {
    return 'Approve as termination requires a scheduled return date.';
  }
  return null;
}

function buildDecisionPayload(
  decisionType: CancellationDecisionType,
  notes: string,
  renegotiationAssignee: string,
  accountAgent: string,
  scheduledReturn: string,
): {
  decision_type: CancellationDecisionType;
  manager_notes: string;
  renegotiation_assignee_id: string | null;
  account_agent_id: string | null;
  scheduled_return_date: string | null;
} {
  return {
    decision_type: decisionType,
    manager_notes: notes.trim(),
    renegotiation_assignee_id: decisionType === 'approve_change' ? renegotiationAssignee : null,
    account_agent_id: decisionType === 'approve_change' ? accountAgent : null,
    scheduled_return_date: decisionType === 'approve_termination' ? scheduledReturn : null,
  };
}

/** Pending deposits a termination would auto-reject — fetched lazily the first
 *  time the manager considers termination. size:100 is the backend max; the
 *  real auto-reject runs server-side on the full set regardless. */
function usePendingTerminationDeposits(
  decisionType: CancellationDecisionType,
  userId: string,
  productId: string,
): {
  pendingDeposits: DepositRequest[] | null;
  depositsError: string | null;
  autoReject: { items: DepositRequest[]; count: number; total: string; currency: string | null } | null;
} {
  const [pendingDeposits, setPendingDeposits] = useState<DepositRequest[] | null>(null);
  const [depositsError, setDepositsError] = useState<string | null>(null);
  useEffect(() => {
    if (decisionType !== 'approve_termination' || pendingDeposits !== null) return;
    let cancelled = false;
    depositsService
      .list({ status: ['pending'], user_id: userId, size: 100 })
      .then((res) => { if (!cancelled) setPendingDeposits(res.items); })
      .catch((err: unknown) => {
        if (!cancelled) setDepositsError(getApiErrorMessage(err, 'Could not load pending deposits.'));
      });
    return () => { cancelled = true; };
  }, [decisionType, pendingDeposits, userId]);
  const autoReject = pendingDeposits ? pendingDepositsForProduct(pendingDeposits, productId) : null;
  return { pendingDeposits, depositsError, autoReject };
}

/** "Approve as change" branch: renegotiation + account agent pickers. */
function ApproveChangeFields({
  agentsError,
  agentsLoading,
  onRetryAgents,
  assigneeOptions,
  renegotiationAssignee,
  onRenegotiationAssignee,
  accountAgent,
  onAccountAgent,
}: {
  readonly agentsError: string | null;
  readonly agentsLoading: boolean;
  readonly onRetryAgents: () => void;
  readonly assigneeOptions: { value: string; label: string }[];
  readonly renegotiationAssignee: string;
  readonly onRenegotiationAssignee: (v: string) => void;
  readonly accountAgent: string;
  readonly onAccountAgent: (v: string) => void;
}): JSX.Element {
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {agentsError ? (
        <div style={{ ...errBoxStyle, marginBottom: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13 }}>{agentsError}</span>
          <button
            type="button"
            onClick={onRetryAgents}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', textDecoration: 'underline', fontSize: 12, flexShrink: 0 }}
          >
            Retry
          </button>
        </div>
      ) : null}
      <div style={twoColStyle}>
        <label style={labelStyle}>
          Renegotiation agent (handles this call)
          {/* */}
          <select
            value={renegotiationAssignee}
            onChange={(e) => onRenegotiationAssignee(e.target.value)}
            disabled={agentsLoading || !!agentsError}
            style={inputStyle}
          >
            <option value="">{agentsLoading ? 'Loading agents…' : '— choose —'}</option>
            {assigneeOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label style={labelStyle}>
          Account agent (long-term owner of the relationship)
          {/* */}
          <select
            value={accountAgent}
            onChange={(e) => onAccountAgent(e.target.value)}
            disabled={agentsLoading || !!agentsError}
            style={inputStyle}
          >
            <option value="">{agentsLoading ? 'Loading agents…' : '— choose —'}</option>
            {assigneeOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
      </div>
    </div>
  );
}

/** "Approve as termination" branch: irreversible warning + the pending-deposit
 *  auto-reject preview + the scheduled return date. */
function ApproveTerminationFields({
  configVersion,
  currencyCode,
  depositsError,
  pendingDeposits,
  autoReject,
  scheduledReturn,
  onScheduledReturn,
}: {
  readonly configVersion: number;
  readonly currencyCode: string;
  readonly depositsError: string | null;
  readonly pendingDeposits: DepositRequest[] | null;
  readonly autoReject: ReturnType<typeof pendingDepositsForProduct> | null;
  readonly scheduledReturn: string;
  readonly onScheduledReturn: (v: string) => void;
}): JSX.Element {
  // Preview exactly which pending deposits will be auto-rejected so the
  // manager isn't deciding blind on an irreversible action.
  let depositPreview: JSX.Element;
  if (depositsError) {
    depositPreview = (
      <p style={{ margin: '0 0 12px', fontSize: 12, color: tokens.errorText }}>
        {depositsError}
      </p>
    );
  } else if (pendingDeposits === null) {
    depositPreview = (
      <p style={{ margin: '0 0 12px', fontSize: 12, color: tokens.textMuted }}>
        Checking for pending deposits…
      </p>
    );
  } else if (autoReject && autoReject.count > 0) {
    depositPreview = (
      <div style={{ margin: '0 0 12px' }}>
        <p style={{ fontSize: 12, fontWeight: 600, color: tokens.errorText }}>
          {autoReject.count} pending deposit{autoReject.count === 1 ? '' : 's'} (
          {formatMoney(autoReject.total, autoReject.currency ?? currencyCode)}) will be
          auto-rejected:
        </p>
        <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12, color: tokens.textMuted }}>
          {autoReject.items.map((d) => (
            <li key={d.id}>
              {formatMoney(d.amount, d.currency_code)}
              {d.reference_number ? ` · ${d.reference_number}` : ''}
            </li>
          ))}
        </ul>
      </div>
    );
  } else {
    depositPreview = (
      <p style={{ margin: '0 0 12px', fontSize: 12, color: tokens.textMuted }}>
        No pending deposits for this product — nothing will be auto-rejected.
      </p>
    );
  }

  return (
    <div style={terminationBoxStyle}>
      <span style={labelTinyStyle}>⚠ Termination — irreversible</span>
      <p style={{ margin: '6px 0 12px', fontSize: 12, color: tokens.errorText }}>
        Retention is computed from the snapshotted product config (v{configVersion}).
      </p>

      {depositPreview}

      <label style={labelStyle}>
        Scheduled return date (when funds hit the client&apos;s bank)
        {/* */}
        <input
          type="date"
          value={scheduledReturn}
          onChange={(e) => onScheduledReturn(e.target.value)}
          style={inputStyle}
        />
      </label>
    </div>
  );
}

function CancellationCard({
  c, me, agents, agentsLoading, agentsError, onRetryAgents, busy, onDecided, onError, setBusy, onForceCancel,
}: CancellationCardProps): JSX.Element {
  // Default the renegotiation agent to the agent who requested the
  // cancellation. The backend stores `cancellation_requested_by` on
  // the commitment row but the existing summary endpoint doesn't
  // surface it; for the MVP, manager picks explicitly when in doubt.
  const [decisionType, setDecisionType] = useState<CancellationDecisionType>('reject');
  const [renegotiationAssignee, setRenegotiationAssignee] = useState<string>('');
  const [accountAgent, setAccountAgent] = useState<string>('');
  const [scheduledReturn, setScheduledReturn] = useState<string>(defaultScheduledReturn());
  const [notes, setNotes] = useState<string>('');

  // Pending deposits for this client, fetched lazily the first time the
  // manager considers termination, so we can show exactly which deposits
  // the termination will auto-reject (filtered to this product below).
  // Pending deposits that a termination would auto-reject (lazy; see hook).
  const { pendingDeposits, depositsError, autoReject } = usePendingTerminationDeposits(
    decisionType, c.user_id, c.product_id,
  );

  const assigneeOptions = useMemo(() => buildAssigneeOptions(me, agents), [me, agents]);

  const submit = async (): Promise<void> => {
    const validationError = validateCancellationDecision(
      decisionType, notes, renegotiationAssignee, accountAgent, scheduledReturn,
    );
    if (validationError) {
      onError(validationError);
      return;
    }

    setBusy(true);
    try {
      const result = await commitmentsService.decideCancellation(
        c.id,
        buildDecisionPayload(decisionType, notes, renegotiationAssignee, accountAgent, scheduledReturn),
      );
      onDecided(buildSuccessMessage(decisionType, result));
    } catch (e) {
      onError(getApiErrorMessage(e, 'Decision failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <strong>{fullName({ first_name: c.user_first_name, last_name: c.user_last_name })}</strong>{' '}
          <span style={{ color: tokens.textMuted, fontSize: 12 }}>{c.user_email}</span>
          <div style={{ color: tokens.textMuted, fontSize: 13, marginTop: 4 }}>
            {c.product_name} · {c.cadence} · {formatMoney(c.amount, c.currency_code)} · config v{c.config_version}
          </div>
        </div>
        <a
          href={`/manager/cases/${c.contract_case_id}`}
          style={{ color: tokens.gold, fontSize: 12 }}
        >
          contract case →
        </a>
      </div>

      {c.cancellation_reason ? (
        <div style={reasonBoxStyle}>
          <span style={labelTinyStyle}>Agent&apos;s reason</span>
          <p style={{ margin: '4px 0 0', fontSize: 13 }}>{c.cancellation_reason}</p>
        </div>
      ) : null}

      <fieldset style={fsStyle}>
        <legend style={{ color: tokens.textMuted, fontSize: 12, padding: '0 6px' }}>
          Decision
        </legend>
        <label style={radioRowStyle}>
          <input
            type="radio"
            name={`decision-${c.id}`}
            checked={decisionType === 'reject'}
            onChange={() => setDecisionType('reject')}
          />
          <span>
            <strong>Reject</strong> — keep the commitment, revert to {c.cancellation_reason ? 'previous status' : 'active/paused'}
          </span>
        </label>
        <label style={radioRowStyle}>
          <input
            type="radio"
            name={`decision-${c.id}`}
            checked={decisionType === 'approve_change'}
            onChange={() => setDecisionType('approve_change')}
          />
          <span>
            <strong>Approve as a change</strong> — cancel current terms, schedule
            renegotiation with new cadence/amount, reassign account agent
          </span>
        </label>
        <label style={radioRowStyle}>
          <input
            type="radio"
            name={`decision-${c.id}`}
            checked={decisionType === 'approve_termination'}
            onChange={() => setDecisionType('approve_termination')}
          />
          <span style={{ color: tokens.errorText }}>
            <strong>Approve as termination</strong> — close the contract on this
            product. Retention applied; balance return scheduled.
          </span>
        </label>
      </fieldset>

      {decisionType === 'approve_change' ? (
        <ApproveChangeFields
          agentsError={agentsError}
          agentsLoading={agentsLoading}
          onRetryAgents={onRetryAgents}
          assigneeOptions={assigneeOptions}
          renegotiationAssignee={renegotiationAssignee}
          onRenegotiationAssignee={setRenegotiationAssignee}
          accountAgent={accountAgent}
          onAccountAgent={setAccountAgent}
        />
      ) : null}

      {decisionType === 'approve_termination' ? (
        <ApproveTerminationFields
          configVersion={c.config_version}
          currencyCode={c.currency_code}
          depositsError={depositsError}
          pendingDeposits={pendingDeposits}
          autoReject={autoReject}
          scheduledReturn={scheduledReturn}
          onScheduledReturn={setScheduledReturn}
        />
      ) : null}

      <label style={labelStyle}>
        Manager notes (5–1000 chars, audited)
        {/* */}
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={1000}
          rows={2}
          style={inputStyle}
        />
      </label>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
        <button
          type="button"
          onClick={submit}
          disabled={busy || notes.trim().length < 5}
          style={decisionType === 'approve_termination' ? btnReject : btnApprove}
        >
          Submit decision
        </button>
        <button
          type="button"
          onClick={onForceCancel}
          disabled={busy}
          style={btnForceCancel}
          title="Bypass the normal flow — fraud / regulatory / etc."
        >
          Force-cancel…
        </button>
      </div>
    </div>
  );
}

function buildSuccessMessage(
  decisionType: CancellationDecisionType,
  result: { follow_up_case_id?: string; reverted_to?: string; return_amount_scheduled?: string },
): string {
  if (decisionType === 'reject') {
    return `Cancellation rejected · commitment reverted to ${result.reverted_to ?? 'prior state'}`;
  }
  if (decisionType === 'approve_change') {
    return `Approved as change · renegotiation case ${result.follow_up_case_id} opened`;
  }
  return `Terminated · ${result.return_amount_scheduled} scheduled to return · case ${result.follow_up_case_id}`;
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  color: tokens.textPrimary,
  fontSize: 13,
};
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 10px',
  borderBottom: `1px solid ${tokens.border}`,
  color: tokens.textMuted,
};
const tdStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: `1px solid ${tokens.border}`,
  verticalAlign: 'top',
};
const h2Style: React.CSSProperties = {
  margin: '0 0 12px 0',
  fontSize: 14,
  color: tokens.textPrimary,
};
const cardStyle: React.CSSProperties = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 12,
  padding: 16,
  display: 'grid',
  gap: 10,
};
const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  fontSize: 12,
  color: tokens.textLabel,
};
const inputStyle: React.CSSProperties = {
  background: tokens.surfaceMuted,
  border: `1px solid ${tokens.border}`,
  borderRadius: 8,
  padding: '8px 10px',
  color: tokens.textPrimary,
};
const twoColStyle: React.CSSProperties = {
  display: 'grid',
  // Two columns when there's room; collapses to one on narrow screens.
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
  gap: 12,
};
const labelTinyStyle: React.CSSProperties = {
  color: tokens.gold,
  fontSize: 10,
  letterSpacing: 1,
  textTransform: 'uppercase',
};
const reasonBoxStyle: React.CSSProperties = {
  background: tokens.surfaceMuted,
  borderRadius: 8,
  padding: 10,
};
const terminationBoxStyle: React.CSSProperties = {
  background: tokens.errorBg,
  border: `1px solid ${tokens.errorBorder}`,
  borderRadius: 8,
  padding: 10,
};
const fsStyle: React.CSSProperties = {
  border: `1px solid ${tokens.border}`,
  borderRadius: 8,
  padding: '6px 12px',
  display: 'grid',
  gap: 6,
};
const radioRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 8,
  fontSize: 13,
  alignItems: 'flex-start',
};
const btnApprove: React.CSSProperties = {
  background: tokens.successBorder,
  color: tokens.navy,
  border: 'none',
  borderRadius: 6,
  padding: '6px 12px',
  marginRight: 6,
  cursor: 'pointer',
  fontWeight: 600,
};
const btnReject: React.CSSProperties = {
  background: tokens.errorBorder,
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  padding: '6px 12px',
  cursor: 'pointer',
  fontWeight: 600,
};
// Visually distinct from `btnReject` (an in-flow rejection) so a
// manager doesn't fat-finger an emergency cancel: dark outline,
// transparent fill — clearly an "escalation" rather than a routine
// negative answer.
const btnForceCancel: React.CSSProperties = {
  background: 'transparent',
  color: tokens.errorText,
  border: `1px solid ${tokens.errorBorder}`,
  borderRadius: 6,
  padding: '6px 12px',
  cursor: 'pointer',
  fontWeight: 600,
};
const errBoxStyle: React.CSSProperties = {
  background: tokens.errorBg,
  border: `1px solid ${tokens.errorBorder}`,
  color: tokens.errorText,
  padding: 12,
  borderRadius: 8,
  marginBottom: 12,
};
const successBoxStyle: React.CSSProperties = {
  background: tokens.successBg,
  border: `1px solid ${tokens.successBorder}`,
  color: tokens.successText,
  padding: 12,
  borderRadius: 8,
  marginBottom: 12,
};
